/**
 * AJAX Newsletter Form Handler
 *
 * Intercepts all forms with [data-ajax-newsletter] and submits them via fetch
 * instead of a full page reload. Compatible with Shopify's hCaptcha integration
 * because we use FormData (which captures all hidden inputs Shopify injects).
 *
 * Expected markup per form:
 *   <form data-ajax-newsletter ...>
 *     <input type="email" name="contact[email]" required>
 *     <button type="submit">…</button>
 *     <p data-ajax-newsletter-success hidden>Thank you!</p>
 *     <p data-ajax-newsletter-error hidden>Something went wrong.</p>
 *   </form>
 */

(function () {
  "use strict";

  /** Timeout (ms) for the fetch request */
  const FETCH_TIMEOUT = 10000;

  /**
   * Attach AJAX behaviour to every [data-ajax-newsletter] form in the DOM.
   * Safe to call more than once (idempotent via a data-flag).
   */
  function init() {
    document.querySelectorAll("form[data-ajax-newsletter]").forEach(bindForm);
  }

  /**
   * @param {HTMLFormElement} form
   */
  function bindForm(form) {
    if (form.dataset.ajaxBound) return; // already wired up
    form.dataset.ajaxBound = "true";

    let isProcessingAjax = false;

    // Override form.submit() - Shopify's hCaptcha calls this after validation
    const nativeSubmit = form.submit.bind(form);
    form.submit = function () {
      // When hCaptcha calls form.submit(), use AJAX instead
      if (!isProcessingAjax) {
        isProcessingAjax = true;
        handleSubmit(form).finally(() => {
          isProcessingAjax = false;
        });
      }
    };

    // Intercept submit event - use capture phase to run before Shopify's handler
    form.addEventListener(
      "submit",
      function (e) {
        // Shopify injects a spam-protection token field for whichever provider is
        // active on customer forms — hCaptcha (h-captcha-response) or Google
        // reCAPTCHA (g-recaptcha-response). Detect EITHER, and read the token from
        // whichever is populated, so we never AJAX-submit a captcha-protected form
        // without its token (Shopify rejects that as spam → "Something went wrong").
        const hCaptchaField = /** @type {HTMLInputElement|null} */ (
          form.querySelector('[name="h-captcha-response"]')
        );
        const gRecaptchaField = /** @type {HTMLInputElement|null} */ (
          form.querySelector('[name="g-recaptcha-response"]')
        );
        const captchaField = hCaptchaField || gRecaptchaField;
        const captchaToken =
          (hCaptchaField && hCaptchaField.value) ||
          (gRecaptchaField && gRecaptchaField.value) ||
          "";

        // Captcha present and already solved → submit via AJAX now.
        if (captchaField && captchaToken) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (!isProcessingAjax) {
            isProcessingAjax = true;
            handleSubmit(form).finally(() => {
              isProcessingAjax = false;
            });
          }
          return;
        }

        // Captcha present but NOT yet solved: hand the event off, untouched, to
        // Shopify's spam-protection handler. It will preventDefault, obtain the
        // token, then call form.submit() — which we override above to submit via
        // AJAX. We must NOT preventDefault / stopPropagation here, or Shopify's
        // handler never runs and no token is ever produced.
        //
        // We deliberately no longer swap form.action to "about:blank" as a guard
        // against a stray native POST. That swap raced its own 100ms restore
        // against Shopify calling form.submit(), so handleSubmit sometimes fetched
        // "about:blank" and surfaced a bogus "Something went wrong"; and where
        // Shopify's handler never bound — a form inside a delayed custom-element
        // modal, e.g. the newsletter popup — the browser navigated to about:blank,
        // a blank "crash" (on a PDP the follow-up reload also dropped ?variant and
        // showed a different colour). Passing the event through untouched means
        // the worst case is an ordinary native POST that still subscribes the
        // user, never a broken navigation.
        if (captchaField && !captchaToken) {
          return;
        }

        // No captcha field at all - use AJAX directly
        e.preventDefault();
        e.stopImmediatePropagation();
        if (!isProcessingAjax) {
          isProcessingAjax = true;
          handleSubmit(form).finally(() => {
            isProcessingAjax = false;
          });
        }
      },
      true, // Capture phase
    );
  }

  /**
   * @param {HTMLFormElement} form
   */
  async function handleSubmit(form) {
    const submitBtn = form.querySelector('[type="submit"]');
    const emailInput = form.querySelector('[name="contact[email]"]');
    const successEl = form.querySelector("[data-ajax-newsletter-success]");
    const errorEl = form.querySelector("[data-ajax-newsletter-error]");

    // Reset previous messages
    if (successEl) successEl.setAttribute("hidden", "");
    if (errorEl) errorEl.setAttribute("hidden", "");

    // Disable button to prevent double-submit
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute("aria-busy", "true");
    }

    try {
      const body = new FormData(form);

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);

      const response = await fetch(form.action, {
        method: "POST",
        body: body,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
        signal: controller.signal,
      });

      clearTimeout(timer);

      /**
       * Shopify customer form returns a 200 HTML page on both success and
       * duplicate-email. A successful submission redirects (302) which fetch
       * follows automatically — the final response URL will contain
       * "customer_posted=true". A challenge (hCaptcha) failure redirects to a
       * challenge page whose URL contains "/challenge".
       */
      const finalUrl = response.url || "";

      if (finalUrl.includes("challenge")) {
        // hCaptcha challenge failed — reload so the user can solve it
        window.location.href = finalUrl;
        return;
      }

      // Treat 200 as success (Shopify returns 200 after redirect)
      if (response.ok) {
        if (successEl) successEl.removeAttribute("hidden");
        if (emailInput) emailInput.value = "";
        form.dispatchEvent(new CustomEvent("newsletter:success", { bubbles: true }));
      } else {
        // Surface Shopify's actual response so 400s / bot protection / duplicate
        // signups can be diagnosed instead of vanishing into a generic error.
        let bodyText = "";
        try { bodyText = await response.text(); } catch (_) {}
        console.warn(
          "[newsletter-ajax] Shopify returned",
          response.status,
          response.statusText,
          "URL:", finalUrl,
          "Body snippet:", bodyText.slice(0, 400),
        );
        showError(errorEl);
        form.dispatchEvent(new CustomEvent("newsletter:error", { bubbles: true }));
      }
    } catch (err) {
      if (err.name === "AbortError") {
        showError(errorEl, "The request timed out. Please try again.");
      } else {
        showError(errorEl);
      }
      form.dispatchEvent(new CustomEvent("newsletter:error", { bubbles: true }));
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute("aria-busy");
      }
    }
  }

  /**
   * @param {HTMLElement|null} el
   * @param {string}          [msg]
   */
  function showError(el, msg) {
    if (!el) return;
    if (msg) el.textContent = msg;
    el.removeAttribute("hidden");
  }

  // Initialise on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-initialise when Shopify Theme Editor adds/moves sections
  if (Shopify.designMode) {
    document.addEventListener("shopify:section:load", init);
  }

  // Expose init so code that re-renders [data-ajax-newsletter] forms dynamically
  // (e.g. the cart drawer replaces #CartDrawer's innerHTML on add-to-cart, which
  // destroys + recreates the cart newsletter form) can re-bind the new form node.
  // init() is idempotent (guarded by data-ajax-bound), so calling it repeatedly is
  // safe. Without this, a re-rendered form would fall back to a native POST and
  // reload the whole page.
  window.NewsletterAjax = { init: init };
})();
